import {
  HttpEvent, HttpHandler, HttpInterceptor, HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, EMPTY } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { LanguageService } from 'src/app/components/admin/points-interest/services/language.service';
import Messages from 'src/app/components/shared/enums/messages.enum';
import { NotificationService } from '../services/notification.service';
import { TokenService } from './../auth/services/token.service';


@Injectable()
export class TokenInterceptor implements HttpInterceptor {

  /**
   * Requisicoes ainda em voo. O `show()` do loader e disparado no BaseService por requisicao,
   * mas o `hide()` era chamado assim que QUALQUER resposta chegava. Com duas ou mais chamadas
   * concorrentes (o caso comum: salvar dispara o PUT e alguma outra consulta em paralelo),
   * a primeira a responder fechava o loading enquanto as demais ainda estavam abertas.
   * Agora so escondemos quando a ultima requisicao termina.
   */
  private pendingRequests = 0;

  /**
   * Timer do `hide()` pendente. O `show()` mora no BaseService e roda de forma SINCRONA na criacao do
   * observable; o contador daqui so sobe no `subscribe`. Num `switchMap` a ordem e: unsubscribe da
   * requisicao anterior (contador zera -> hide) e SO DEPOIS a nova e criada (show) e assinada. Como o
   * `hide()` do ngx-spinner e agendado num `setTimeout`, o `show:false` do cancelamento chegava no
   * macrotask seguinte e apagava o overlay da requisicao que acabara de comecar — o loading sumia com
   * a chamada ainda em voo e a tela so atualizava depois, sem spinner.
   * Agendar o hide e cancela-lo quando uma nova requisicao entra elimina a janela: encadeamentos
   * dentro do mesmo tick mantem o loading aberto do inicio ao fim.
   */
  private hideTimeout: any = null;

  constructor(private loaderService: NgxSpinnerService,
    private tokenService: TokenService,
    private languageService: LanguageService,
    private notificationService: NotificationService,
    private translate: TranslateService) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.tokenService.getToken('userToken');
    const language = this.languageService.getCurrentLanguage();
    const languageId = language && language?.id ? language?.id?.toString() : '1';
    if (!!token) {
      if (request.url !== 'https://jsonip.com') {
        const clonedReq = request.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
            Platform: 'Backoffice',
            Language: languageId,
            InstitutionId: this.tokenService.getToken(this.tokenService.TOKEN_INTITUTION_ID) + ''
          }
        });
        return this.verifyRequest(clonedReq, next);
      } else {
        return next.handle(request.clone());
      }
    } else {
      return this.verifyRequest(request.clone(), next);
    }
  }

  private verifyRequest(requisicao: HttpRequest<any>, handlerHttp: HttpHandler) {
    this.pendingRequests++;
    // Chegou requisicao nova antes do hide agendado disparar: o loading continua valendo.
    this.cancelPendingHide();
    return handlerHttp.handle(requisicao).pipe(
      catchError(err => {
        if (err.status == 403) {
          this.notificationService.messageError(this.translate.instant(Messages.FORBIDEN_REQUEST));
          return EMPTY;
        } else {
          throw err;
        }
      }),
      // FINALIZAR LOADING;
      // O decremento fica no finalize (e nao no sucesso/erro) porque ele tambem dispara quando a
      // subscription e cancelada - switchMap trocando de stream, componente destruido no meio da
      // chamada. Se contassemos so as respostas, uma requisicao cancelada deixaria o contador
      // travado acima de zero e o loading nunca mais fecharia.
      finalize(() => {
        this.pendingRequests = Math.max(0, this.pendingRequests - 1);
        if (this.pendingRequests === 0) {
          this.scheduleHide();
        }
      })
    );
  }

  // Adia o hide por um macrotask (ver hideTimeout). Se nesse intervalo outra requisicao subir o
  // contador, o timer e cancelado e o loading nunca chega a piscar; a recheca do contador cobre o
  // caso de a requisicao nova comecar e terminar dentro da propria janela.
  private scheduleHide(): void {
    this.cancelPendingHide();
    this.hideTimeout = setTimeout(() => {
      this.hideTimeout = null;
      if (this.pendingRequests === 0) {
        this.loaderService.hide();
      }
    });
  }

  private cancelPendingHide(): void {
    if (this.hideTimeout !== null) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }
}
